/**
 * Session engine — thin adapter over oidc-client-ts `UserManager`. The
 * adapter surface (ensureFresh / isActive / isDead / subscribe / …) is the
 * stable contract apiClient and the FDTR chrome consume; the library owns
 * PKCE, the code exchange, localStorage persistence (shared by every tab of
 * the origin, survives a browser restart) and the proactive refresh-token
 * renewal. The session lives until Cognito's refresh token expires or is
 * revoked — a new tab or a deep link never re-walks SSO before that. `invalid_grant` flips a terminal dead
 * flag that drives the sign-in fallback UI.
 */

import {
  ErrorResponse,
  User,
  UserManager,
  WebStorageStateStore,
} from 'oidc-client-ts';

export interface TokenSnapshot {
  idToken: string | null;
  expiresAtMs: number | null;
  /** Silent renewal is possible — a refresh token is held. */
  active: boolean;
  /** Refresh permanently failed — only a full SSO round-trip recovers. */
  dead: boolean;
}

interface TokenManagerConfig {
  /** e.g. https://auth.sandbox.westernbulk.cloud */
  authDomain: string;
  /** Must equal the id_token `iss` claim (Cognito user-pool issuer). */
  issuer: string;
  clientId: string;
  /** Byte-identical to the authorize call's redirect_uri. */
  redirectUri: string;
  /** Expose window.wbAuthDebug (non-prod only). */
  exposeDebugHandle?: boolean;
}

// Renew 5 min before expiry; treat tokens with <60s left as already stale.
const REFRESH_LEAD_S = 5 * 60;
const MIN_VALID_MS = 60_000;

const OAUTH_SCOPE = 'aws.cognito.signin.user.admin email openid';

/** Set by signOut; while present the app shows the signed-out screen instead
 *  of auto-starting SSO (shared across tabs, cleared by the next sign-in). */
const SIGNED_OUT_KEY = 'wb.auth.signedOut';

let um: UserManager | null = null;
let currentUser: User | null = null;
let dead = false;
let refreshInFlight: Promise<string> | null = null;
const listeners = new Set<(s: TokenSnapshot) => void>();

function snapshot(): TokenSnapshot {
  return {
    idToken: currentUser?.id_token ?? null,
    expiresAtMs: currentUser?.expires_at != null ? currentUser.expires_at * 1000 : null,
    active: !!currentUser?.refresh_token && !dead,
    dead,
  };
}

function notify(): void {
  const s = snapshot();
  listeners.forEach((fn) => fn(s));
}

function markDead(reason: string): void {
  dead = true;
  console.error(`tokenManager: session dead — ${reason}`);
  // Drop the persisted session so no other tab (or the next open) adopts a
  // refresh token the server has already rejected; they re-walk SSO instead.
  void um?.removeUser().catch(() => undefined);
  notify();
}

function classifyRenewError(err: unknown): void {
  const code = err instanceof ErrorResponse ? err.error ?? '' : '';
  const msg = err instanceof Error ? err.message : String(err);
  if (code === 'invalid_grant' || /invalid_grant/.test(msg)) {
    // Refresh token expired/revoked — terminal until a full re-login.
    markDead('refresh token rejected (invalid_grant)');
  } else if (code !== '' && code !== 'temporarily_unavailable') {
    // Misconfiguration (invalid_client etc.) — retrying cannot succeed.
    markDead(`token endpoint error: ${code} ${msg.slice(0, 200)}`);
  }
  // Network/5xx/429 → transient: stay alive; the next call retries.
}

function refreshSingleFlight(): Promise<string> {
  if (refreshInFlight) return refreshInFlight;
  if (!um) return Promise.reject(new Error('tokenManager: not configured'));
  refreshInFlight = um
    .signinSilent()
    .then((user) => {
      if (!user || !user.id_token) {
        markDead('silent renew returned no id_token');
        throw new Error('token refresh failed: no id_token');
      }
      currentUser = user;
      dead = false;
      notify();
      return user.id_token;
    })
    .catch((err: unknown) => {
      classifyRenewError(err);
      throw err instanceof Error ? err : new Error(String(err));
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

/** Wire endpoint/client config (idempotent; call before any signin/refresh). */
export function configure(c: TokenManagerConfig): void {
  if (um) return;
  um = new UserManager({
    authority: c.issuer,
    metadata: {
      issuer: c.issuer,
      authorization_endpoint: `${c.authDomain}/oauth2/authorize`,
      token_endpoint: `${c.authDomain}/oauth2/token`,
      revocation_endpoint: `${c.authDomain}/oauth2/revoke`,
      end_session_endpoint: `${c.authDomain}/logout`,
    },
    client_id: c.clientId,
    redirect_uri: c.redirectUri,
    response_type: 'code',
    scope: OAUTH_SCOPE,
    extraQueryParams: { identity_provider: 'LOGIN' },
    // Shared across same-origin tabs and browser restarts; bounded by the
    // refresh-token validity on the Cognito app client.
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    automaticSilentRenew: true,
    accessTokenExpiringNotificationTimeInSeconds: REFRESH_LEAD_S,
    monitorSession: false, // Cognito sends X-Frame-Options: DENY
    loadUserInfo: false,
  });
  um.events.addUserLoaded((user) => {
    currentUser = user;
    dead = false;
    notify();
  });
  um.events.addSilentRenewError((err) => {
    classifyRenewError(err);
  });
  // The store is shared: a sign-out (or dead-session cleanup) in another tab
  // removes the user key there and must end this tab's session too; a
  // sign-in elsewhere (which clears the signed-out flag first) brings a
  // session-less tab back in. Silent renewals also rewrite the key, hence
  // the no-session guard, and a tab still flagged signed out stays put so
  // it cannot reload on every renewal elsewhere.
  const userKey = `oidc.user:${c.issuer}:${c.clientId}`;
  window.addEventListener('storage', (e: StorageEvent) => {
    const removedElsewhere = e.key === userKey && e.newValue === null;
    const signedOutElsewhere = e.key === SIGNED_OUT_KEY && e.newValue !== null;
    const signedInElsewhere =
      e.key === userKey && e.newValue !== null && !currentUser && !isSignedOut();
    if (removedElsewhere || signedOutElsewhere || signedInElsewhere) {
      window.location.reload();
    }
  });
  if (c.exposeDebugHandle && typeof window !== 'undefined') {
    (window as unknown as Record<string, unknown>).wbAuthDebug = {
      state: snapshot,
      forceRefresh: refreshSingleFlight,
      // Re-stamps the token as entering the renewal window (lead + 2s), so
      // the library's own expiring-timer → silent-renew pipeline fires in
      // ~2s — the same path the real ~55-min mark takes. An already-past
      // expiry would NOT fire the timer (learned live), hence the offset.
      expireNow: (): void => {
        if (!um || !currentUser) return;
        currentUser.expires_at =
          Math.floor(Date.now() / 1000) + REFRESH_LEAD_S + 2;
        void um.storeUser(currentUser).then(() => {
          um?.events.load(currentUser as User);
        });
      },
    };
  }
}

export function getIdToken(): string | null {
  return currentUser?.id_token ?? null;
}

/** Silent renewal available (refresh token held and not dead). */
export function isActive(): boolean {
  return !!currentUser?.refresh_token && !dead;
}

export function isDead(): boolean {
  return dead;
}

/** Refresh regardless of local expiry — for tokens the server just rejected
 *  despite a valid-looking `exp` (revocation, skew). Single-flight. */
export function forceRefresh(): Promise<string> {
  if (dead) return Promise.reject(new Error('tokenManager: session dead'));
  if (!currentUser?.refresh_token) {
    return Promise.reject(new Error('tokenManager: no refresh token'));
  }
  return refreshSingleFlight();
}

/** Currently-valid id token, refreshing first when inside the staleness
 *  margin. Single-flight: concurrent callers share one refresh. */
export function ensureFresh(): Promise<string> {
  if (dead) return Promise.reject(new Error('tokenManager: session dead'));
  const u = currentUser;
  if (!u) return Promise.reject(new Error('tokenManager: no session'));
  if (!u.refresh_token) {
    // Passive mode (legacy/implicit boot) — hand back whatever we hold.
    return u.id_token
      ? Promise.resolve(u.id_token)
      : Promise.reject(new Error('tokenManager: no session'));
  }
  const msLeft = (u.expires_at ?? 0) * 1000 - Date.now();
  if (u.id_token && msLeft > MIN_VALID_MS) return Promise.resolve(u.id_token);
  return refreshSingleFlight();
}

/** Subscribe to token changes; returns the unsubscribe function. */
export function subscribe(fn: (s: TokenSnapshot) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/* ──────────────── Boot-path helpers (consumed by AuthContext) ──────────────── */

/** Start the code+PKCE login redirect, carrying `stateParams` through SSO. */
export function signinRedirect(stateParams: string): Promise<void> {
  if (!um) return Promise.reject(new Error('tokenManager: not configured'));
  clearSignedOut();
  return um.signinRedirect({ state: stateParams });
}

export function isSignedOut(): boolean {
  try {
    return window.localStorage.getItem(SIGNED_OUT_KEY) !== null;
  } catch {
    return false;
  }
}

export function clearSignedOut(): void {
  try {
    window.localStorage.removeItem(SIGNED_OUT_KEY);
  } catch {
    /* storage blocked — nothing to clear */
  }
}

/** Explicit sign-out: revoke the refresh token server-side, drop the stored
 *  session, flag the browser as signed out, then end the Cognito hosted
 *  session. Other tabs follow via the storage event. Never throws before the
 *  local session is gone — a failed revocation must not keep the user in. */
export async function signOut(): Promise<void> {
  if (!um) throw new Error('tokenManager: not configured');
  try {
    await um.revokeTokens(['refresh_token']);
  } catch (err) {
    console.warn('tokenManager: refresh-token revocation failed', err);
  }
  // Flag first: other tabs reload on it and must already find it set when
  // they boot, so none of them can re-enter SSO on the still-live cookie.
  try {
    window.localStorage.setItem(SIGNED_OUT_KEY, String(Date.now()));
  } catch {
    /* storage blocked — the in-memory session still goes below */
  }
  await um.removeUser();
  currentUser = null;
  notify();
  const { client_id, redirect_uri, metadata } = um.settings;
  const logout = new URL(metadata?.end_session_endpoint ?? '');
  logout.searchParams.set('client_id', client_id);
  logout.searchParams.set('logout_uri', redirect_uri);
  window.location.assign(logout.toString());
}

/** Redeem the `?code=&state=` callback. Single-use — boot calls this once. */
export async function completeSigninCallback(): Promise<{
  idToken: string;
  stateParams: string | null;
}> {
  if (!um) throw new Error('tokenManager: not configured');
  const user = await um.signinRedirectCallback();
  if (!user.id_token) throw new Error('signin callback returned no id_token');
  currentUser = user;
  dead = false;
  notify();
  return {
    idToken: user.id_token,
    stateParams: typeof user.state === 'string' ? user.state : null,
  };
}

/** Adopt the session persisted in this browser (new tab, F5, restart). Renews
 *  an expired-but-refreshable session before answering. Null → no usable session. */
export async function adoptStoredUser(): Promise<string | null> {
  if (!um) return null;
  const user = await um.getUser();
  if (!user) return null;
  currentUser = user;
  dead = false;
  um.events.load(user); // arms the automatic-renew timers
  const msLeft = (user.expires_at ?? 0) * 1000 - Date.now();
  if (user.id_token && msLeft > MIN_VALID_MS) return user.id_token;
  if (!user.refresh_token) return null;
  try {
    return await refreshSingleFlight();
  } catch {
    return null;
  }
}
